import React, { useState, useContext, useEffect } from 'react';
import { To, useNavigate } from 'react-router-dom';
import { useMyPlanSOB, useMySSOUrl, DocumentType } from '@sentara-health/sentara-api-hooks-core';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import {
  useDeviceType,
  DeviceTypes,
  errorhandler,
  MyPlanTabs,
  NavigateConst,
  domainForSegment,
  generateCustomAdobeContentTitle,
  ANALYTICS_MESSAGES,
} from 'common';
import { GlobalContext, isAvMed } from 'context';
import { saveAs } from 'file-saver';
import { t } from 'i18next';
import { Loader, Link } from '@sentara-health/optimahealth_web';

type DashboardLinkAction =
  | { kind: 'sso'; type?: string }
  | { kind: 'redirect'; typeOrUrl?: string }
  | { kind: 'open'; url?: string }
  | { kind: 'visionCarePdf'; fileName: string };

type DashboardLinkItem = {
  id: React.Key | null | undefined;
  to: To;
  onclick?: DashboardLinkAction | null;
  toggle: boolean;
  title: string;
  target?: string;
  referrerPolicy?: string;
};

type DashboardLinksProps = {
  data: DashboardLinkItem[];
  closeModal?: () => void;
  showContent?: boolean;
  getAnalyticsEventType?: (item: DashboardLinkItem, index: number) => string;
};

const DashboardLinks: React.FC<DashboardLinksProps> = ({
  data,
  closeModal,
}) => {
  const { getFeature, isMemberTabDefault, setIsDesignatedTabShow, ssoEndPoints } = useContext(GlobalContext);
  const { MyPlanSOBType } = useMyPlanSOB();
  const { SSOUrlType } = useMySSOUrl();
  const { contentListingItemClicked } = useAdobeAnalytics();
  const [loader, setLoader] = useState(false);
  const deviceType = useDeviceType();
  const navigate = useNavigate();
  const isDesktop = deviceType === DeviceTypes.DESKTOP;
  const handleLinkClick = () => {
    closeModal?.();
  };
  const getAPIUrl = async (type: string) => {
    setLoader(true);
    const res = await SSOUrlType(type);
    if (res?.data?.ssoUrl) {
      openUrl(res?.data?.ssoUrl);
    } else if (res?.errorCode) {
      errorhandler(res?.errorCode);
    }
    setLoader(false);
  };

  // Function to handle Visio Care API Call
  const VisionCareSSOUrl = async (viewCarePlan: string) => {
    const data = await MyPlanSOBType(DocumentType.VISION_CARE);
    if (data?.errorCode !== undefined) {
      errorhandler(data?.errorCode);
      setLoader(false);
    } else {
      pdfConverter(data, viewCarePlan);
    }
  };

  // Function to convert data to PDF and trigger download
  const pdfConverter = (data: any, fileName: string) => {
    let blob;
    const filename = fileName;
    // converting the base64 resposne to pdf
    blob = new Blob([data], { type: 'application/pdf' });
    saveAs(blob, filename);
    setLoader(false);
  };

  const openUrl = (url: string | undefined) => {
    setLoader(false);
    window.open(url, '_blank');
  };
  const apiTypeMapping: Record<string, string | (() => void)> = {
    VisionCare: 'VisionCareSSOUrl',
    // New SSO integrations for release_17.3.2
    DentalDominion: () => openUrl(ssoEndPoints?.DentalDominion),
    ManageRenewPolicy: () => openUrl(ssoEndPoints?.manageRenewPolicy),
    ManageHSA: () => openUrl(ssoEndPoints?.healthSavingAccounts),
    ManageHRA: () => openUrl(ssoEndPoints?.HealthReimbursementAccount),
    ViewCarePlanJiva: () => openUrl(ssoEndPoints?.ViewCarePlan),
    calculateTreatmentCost: 'CalculateTreatmentCostSSOUrl',
    WellsFargo: () => { isAvMed() && getFeature?.PayMonthlyPremiumPage ? navigate('/home/pay-monthly-premium') : redirecttoUrl(ssoEndPoints?.PayMonthlyPremium) },
    PharmacyResources: getFeature?.PharmacyBenefitsManager
      ? 'EsiRx?source=orderrx'
      : 'OptumRx',
    ScheduleLabWork: () => openUrl(ssoEndPoints?.scheduleLabWorkUrl),
    HCSEmployeeHealthWellnessCtr: () => openUrl(ssoEndPoints?.HCSEmployeeHealthWellnessURL),
    VirtualVisit: ssoEndPoints?.VirtualVisitSSO,
    TravelAssistance: 'AssistAmerica',
    Formulary: getFeature?.PharmacyBenefitsManager
      ? 'EsiRx?source=orderrx'
      : 'OptumRx',
    PersonalHealthRecord: 'WellnessToolSSOUrl?source=phr',
    PersonalHealthAssessment: 'WellnessToolSSOUrl?source=pha',
    WellnessTools: 'WellnessToolSSOUrl?source=wellness',
    CalculateTreatmentCostUrl: () => redirecttoUrl(ssoEndPoints?.CalculateTreatmentCostUrl),
  };
  const trackAnalyticsClick = (item: DashboardLinkItem, index: number) => {
  const customTitle = generateCustomAdobeContentTitle(item.title, ANALYTICS_MESSAGES.SIDE_NAV_LINK_CLICK_SUFFIX);
  contentListingItemClicked({
    segment: `${domainForSegment}`,
    contentListing: {
      eventType: item.title,
      listing: {
        content: [
          {
            contentId: (101 + index).toString(),
            contentTitle: customTitle,
            itemPosition: index + 1,
          },
        ],
      },
    },
  });
};
  const onItemClick = (e: any) => {
    const item = e;
    if (item) {
      const apiType = apiTypeMapping[item];
      if (typeof apiType === 'string') {
        if (item === t('common.viewCarePlanCase')) {
          if (getFeature?.JivaCarePlan) {
            getAPIUrl(apiType);
          } else {
            VisionCareSSOUrl(t('common.viewCarePlanCase'));
            setLoader(true);
          }
        } else {
          getAPIUrl(apiType);
        }
      } else if (typeof apiType === 'function') {
        apiType();
      } else {
        setLoader(false);
      }
    }
  };

  // Redirect to SSO URL 
  const redirecttoUrl = (urltype: string) => {
    if (isAvMed()) {
      openUrl(urltype);
    } else {
      getAPIUrl(urltype);
    }
  };
  const [listClassName, setListClassName] = useState('');
  useEffect(() => {
    if (window.location.pathname === NavigateConst.yourPlan) {
      let defaultURLLink = NavigateConst.myPlan + (isMemberTabDefault ? MyPlanTabs.memberDetailID : MyPlanTabs.overViewID);
      setListClassName(
        defaultURLLink
      );
    }
    else {
      setListClassName(
        window.location.pathname
      );
    }
  }, [window.location.pathname, window.location.search]);

  return (
    <ul className="list-group link-list">
      {loader && <Loader />}
      {data.map(
        (item: DashboardLinkItem, index: number) => (
          <React.Fragment key={item.id}>
            {item.toggle === true && (
              <li className="list-group-item list-group-item-action">
                <Link
                  className={`${listClassName === item.to ? 'active-link-list' : ''
                    }`}
                  referrerPolicy={item.referrerPolicy ?? undefined}
                  to={item.to}
                  variant="sidebarmenu_link"
                  data-testid={`${item.title?.charAt(0).toLowerCase() + item.title.slice(1)
                    }Menu`.replace(/\s/g, '')}
                  onClick={() => {
                    trackAnalyticsClick(item, index);
                    setIsDesignatedTabShow(false);
                    item.onclick && onItemClick(item.onclick);
                    !isDesktop && handleLinkClick();
                  }}
                  target={item.target ?? '_self'}
                >
                  {item.title}
                </Link>
              </li>
            )}
          </React.Fragment>
        )
      )}
    </ul>
  );
};
export default DashboardLinks;
